import React, { useEffect } from 'react'
import { useQuery } from 'react-query'
import { facebookService, useGetBusinessID } from '@marketing-milk/frontend'
import {
  AdCreative,
  AdCreativeType,
  CALLS_CALL_TO_ACTION,
  CREATIVE_IMPORT_LIMITS,
  CREATIVE_MEDIA_EDIT_LIMITS,
  CreativeCopyPatch,
  DISPLAY_LINK_MAX_LENGTH,
  FBCallToAction,
  isHttpUrl,
  LEAD_FORM_CALLS_TO_ACTION,
  LeadCard,
  normalizeCallPhoneNumber,
  SUPPORTED_CALL_TO_ACTIONS,
} from '@marketing-milk/interfaces'
import { useGetLeadCardsForBusiness } from 'hooks/facebook/get-lead-cards'
import { isDynamicCreative } from '../../campaigns/helpers/campaign.helpers'
import { CopyPool, CopyPoolName, DynamicCombination } from './dynamicCombination'
import { DynamicPoolPicker } from './DynamicPoolPicker'
import { ActionButton, IconButton, MediaTile, mediaEyebrowStyle } from './MediaControls'
import { DraftCard, DraftMedia, MediaEditing, mediaThumb } from './mediaDraft'
import {
  CopyTextField,
  draftLeadCardID,
  withCallToAction,
  withLeadCard,
  withText,
} from './copyDraft'
import { modalInputStyle } from '../components/PacketModalShell'
import { dark, status as statusColors, type as typo } from '../tokens'

/** The media the editor shows, and what may be done to it. */
export interface MediaEditor {
  /** Single ads only. */
  single?: DraftMedia
  /** Carousels only, saved and pending cards together. */
  cards: DraftCard[]
  selectedCardKey?: string
  onSelectCard: (key: string) => void
  onEditCard: (
    card: DraftCard,
    changes: { headline?: string; description?: string; url?: string }
  ) => void
  /** Absent for a reviewer who may edit copy but not upload. */
  editing?: MediaEditing
}

interface Props {
  creative: AdCreative
  draft: CreativeCopyPatch
  onChange: (next: CreativeCopyPatch) => void
  /** The lead form just picked, so the journey can show it before it is saved. */
  onPickLeadCard: (leadCard?: LeadCard) => void
  media: MediaEditor
  /** No edit may be started — published, or a save is in flight. */
  disabled: boolean
  /** Read-only for a standing reason, stated against the fields. */
  disabledReason?: string
  /** Dynamic only — the pools list doubles as the preview's combination picker. */
  dynamic?: {
    assets: DraftMedia[]
    pools: CopyPool[]
    at: DynamicCombination
    onPick: (next: DynamicCombination) => void
  }
}

/**
 * The ad's content, as fields, beside the ad it redraws.
 *
 * Field-for-field the same copy set the creative importer offers, with the
 * media rows alongside it. The arrangement is the importer's (labelled
 * fields, char counts, the format deciding which fields apply); the components
 * deliberately are not: that surface is MUI on white and this modal is dark and
 * inline-styled, so importing its `TextField`s would mean theming MUI into a
 * dialog that has no theme.
 */
export function ReviewCopyEditor({
  creative,
  draft,
  onChange,
  onPickLeadCard,
  media,
  disabled,
  disabledReason,
  dynamic,
}: Props) {
  const set = (changes: Partial<CreativeCopyPatch>) => onChange({ ...draft, ...changes })
  const setText = (field: CopyTextField) => (value: string) =>
    onChange(withText(draft, creative, field, value))

  const isCarousel = creative.format === AdCreativeType.carousel
  const isDynamic = isDynamicCreative(creative)
  // A carousel's headline and description live on its cards; a dynamic ad's live
  // in its pools. Neither has a creative-level one to edit.
  const hasSingleFields = !isCarousel && !isDynamic

  // Mirrors the server: with a lead form every button has to open it, so no other
  // is offered and "No button" isn't either. Call Now dials instead, so it is a
  // single ad's alone and never beside a form.
  const hasLeadCard = draftLeadCardID(draft, creative) !== undefined
  const callsToAction = hasLeadCard
    ? LEAD_FORM_CALLS_TO_ACTION
    : hasSingleFields
    ? [CALLS_CALL_TO_ACTION, ...SUPPORTED_CALL_TO_ACTIONS]
    : SUPPORTED_CALL_TO_ACTIONS
  // An ad saved before either rule keeps its button listed, so the select never
  // shows a value other than the one it holds.
  const ctaOptions =
    draft.callToAction && !callsToAction.includes(draft.callToAction)
      ? [draft.callToAction, ...callsToAction]
      : callsToAction

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {disabledReason && (
        <p
          style={{
            margin: 0,
            fontFamily: typo.family.sans,
            fontSize: typo.size.body,
            lineHeight: 1.5,
            color: statusColors.warn,
          }}
        >
          {disabledReason}
        </p>
      )}

      <Field
        label="Creative name"
        value={draft.name ?? ''}
        disabled={disabled}
        maxLength={CREATIVE_IMPORT_LIMITS.creativeNameMaxLength}
        note={(draft.name ?? '').trim() ? undefined : 'A creative name is required.'}
        onChange={setText('name')}
      />

      {!isCarousel && !isDynamic && <SingleMediaField media={media} />}

      {isDynamic && dynamic ? (
        <DynamicPoolPicker
          assets={dynamic.assets}
          pools={dynamic.pools}
          at={dynamic.at}
          onPick={dynamic.onPick}
          editing={{
            disabled,
            media: media.editing,
            callsToAction,
            onChangePool: (pool: CopyPoolName, values: string[]) =>
              set({
                pools: {
                  ...draft.pools,
                  [pool]: pool === 'callToActions' ? (values as FBCallToAction[]) : values,
                },
              }),
          }}
        />
      ) : (
        <>
          <Field
            label="Primary text"
            value={draft.primaryText ?? ''}
            disabled={disabled}
            rows={4}
            recommendedMax={CREATIVE_IMPORT_LIMITS.primaryTextRecommendedMax}
            onChange={setText('primaryText')}
          />
          {hasSingleFields && (
            <>
              <Field
                label="Headline"
                value={draft.headline ?? ''}
                disabled={disabled}
                maxLength={CREATIVE_IMPORT_LIMITS.headlineMaxLength}
                recommendedMax={CREATIVE_IMPORT_LIMITS.headlineRecommendedMax}
                onChange={setText('headline')}
              />
              <Field
                label="Description"
                value={draft.description ?? ''}
                disabled={disabled}
                recommendedMax={CREATIVE_IMPORT_LIMITS.descriptionRecommendedMax}
                onChange={setText('description')}
              />
              <Field
                label="Graphic text"
                value={draft.graphicText ?? ''}
                disabled={disabled}
                onChange={setText('graphicText')}
              />
            </>
          )}
          <Labelled label="Call to action">
            <select
              aria-label="Call to action"
              value={draft.callToAction ?? ''}
              disabled={disabled}
              onChange={e =>
                onChange(
                  withCallToAction(
                    draft,
                    creative,
                    e.target.value ? (e.target.value as FBCallToAction) : null
                  )
                )
              }
              style={modalInputStyle(dark)}
            >
              {!hasLeadCard && <option value="">No button</option>}
              {ctaOptions.map(cta => (
                <option key={cta} value={cta}>
                  {cta.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </Labelled>
          {draft.callToAction === CALLS_CALL_TO_ACTION && (
            <CallPhoneNumberField
              value={draft.callPhoneNumber ?? ''}
              disabled={disabled}
              onChange={setText('callPhoneNumber')}
            />
          )}
        </>
      )}

      <Field
        label="Destination URL"
        value={draft.url ?? ''}
        disabled={disabled}
        maxLength={CREATIVE_IMPORT_LIMITS.destinationUrlMaxLength}
        // A warning, not a block: creatives predating any URL validation carry a
        // scheme-less host, and refusing to save one would mean a headline typo
        // couldn't be fixed without also rewriting the ad's link.
        note={
          (draft.url ?? '').trim() && !isHttpUrl((draft.url ?? '').trim())
            ? 'This does not look like a full URL — it should start with http:// or https://.'
            : undefined
        }
        onChange={setText('url')}
      />
      <Field
        label="Display URL"
        value={draft.displayLink ?? ''}
        disabled={disabled}
        maxLength={DISPLAY_LINK_MAX_LENGTH}
        onChange={setText('displayLink')}
      />

      <LeadCardField
        value={draftLeadCardID(draft, creative)}
        disabled={disabled}
        onChange={(leadCardID, leadCard) => {
          onPickLeadCard(leadCard)
          onChange(withLeadCard(draft, creative, leadCardID))
        }}
      />

      {isCarousel && <SlideFields media={media} disabled={disabled} />}
    </div>
  )
}

/**
 * The single ad's media, above the copy it belongs to. Replace only: a single ad
 * has exactly one asset, so there is nothing to add or remove — though an ad made
 * blank has none yet to replace.
 */
function SingleMediaField({ media }: { media: MediaEditor }) {
  const editing = media.editing
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={mediaEyebrowStyle}>Media</span>
        {editing && (
          <ActionButton
            label={media.single ? 'Replace…' : 'Upload…'}
            disabled={editing.disabled || editing.uploading}
            onClick={() => editing.onUpload({ kind: 'single' })}
            style={{ marginLeft: 'auto' }}
          />
        )}
      </div>
      <MediaTile
        thumb={mediaThumb(media.single)}
        size={60}
        selected
        label="Ad media"
        onClick={() => undefined}
      />
    </div>
  )
}

/**
 * One carousel card's copy at a time, picked by its own thumbnail — with the row
 * of thumbnails doubling as where cards are added, replaced and removed.
 */
function SlideFields({ media, disabled }: { media: MediaEditor; disabled: boolean }) {
  const { cards, editing } = media
  const selected = cards.find(card => card.key === media.selectedCardKey) ?? cards[0]
  const position = Math.max(cards.indexOf(selected), 0) + 1

  const atCap = cards.length >= CREATIVE_MEDIA_EDIT_LIMITS.carouselSlidesMax
  const atFloor = cards.length <= CREATIVE_MEDIA_EDIT_LIMITS.carouselSlidesMin
  const mediaBusy = !editing || editing.disabled || editing.uploading

  const edit = (changes: { headline?: string; description?: string; url?: string }) =>
    selected && media.onEditCard(selected, changes)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={mediaEyebrowStyle}>
          Cards · {cards.length}/{CREATIVE_MEDIA_EDIT_LIMITS.carouselSlidesMax}
        </span>
        {editing && (
          <ActionButton
            label="Add card"
            disabled={mediaBusy || atCap}
            title={
              atCap
                ? `A carousel can have at most ${CREATIVE_MEDIA_EDIT_LIMITS.carouselSlidesMax} cards.`
                : undefined
            }
            onClick={() => editing.onUpload({ kind: 'add-card' })}
            style={{ marginLeft: 'auto' }}
          />
        )}
      </div>

      {/* Tabs rather than a stacked list: ten cards of three fields each would
        push the creative-level copy off the column, and the card you're editing
        is the only one whose fields you need. The thumbnail is the label —
        "Card 3" doesn't say which card you are on when the whole job is matching
        copy to an image. */}
      <div role="group" aria-label="Cards" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {cards.map((card, index) => (
          <MediaTile
            key={card.key}
            thumb={mediaThumb(card.media)}
            size={48}
            selected={card.key === selected.key}
            label={`Card ${index + 1}`}
            onClick={() => media.onSelectCard(card.key)}
          />
        ))}
      </div>

      {/* The server refuses to save a carousel short of its floor, so an ad made
        blank says so while its first cards go on, rather than failing the save. */}
      {editing && cards.length < CREATIVE_MEDIA_EDIT_LIMITS.carouselSlidesMin && (
        <span
          style={{
            fontFamily: typo.family.sans,
            fontSize: typo.size.eyebrow,
            color: dark.textMuted,
          }}
        >
          {`Add at least ${CREATIVE_MEDIA_EDIT_LIMITS.carouselSlidesMin} cards to save them.`}
        </span>
      )}

      {editing && selected && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ActionButton
            label="Replace…"
            disabled={mediaBusy}
            onClick={() => editing.onUpload({ kind: 'card', card: selected })}
          />
          <IconButton
            icon="trash"
            label={`Remove card ${position}`}
            disabled={mediaBusy || atFloor}
            title={
              atFloor
                ? `A carousel needs at least ${CREATIVE_MEDIA_EDIT_LIMITS.carouselSlidesMin} cards.`
                : `Remove card ${position}`
            }
            onClick={() => editing.onRemoveCard(selected)}
          />
        </div>
      )}

      {selected && (
        <>
          <Field
            label={`Card ${position} headline`}
            value={selected.headline}
            disabled={disabled}
            maxLength={CREATIVE_IMPORT_LIMITS.headlineMaxLength}
            recommendedMax={CREATIVE_IMPORT_LIMITS.headlineRecommendedMax}
            onChange={headline => edit({ headline })}
          />
          <Field
            label={`Card ${position} description`}
            value={selected.description}
            disabled={disabled}
            maxLength={CREATIVE_IMPORT_LIMITS.headlineMaxLength}
            recommendedMax={CREATIVE_IMPORT_LIMITS.descriptionRecommendedMax}
            onChange={description => edit({ description })}
          />
          <Field
            label={`Card ${position} link`}
            value={selected.url}
            disabled={disabled}
            maxLength={CREATIVE_IMPORT_LIMITS.destinationUrlMaxLength}
            note={selected.url.trim() ? undefined : "Empty — the ad's destination URL is used."}
            onChange={url => edit({ url })}
          />
        </>
      )}
    </div>
  )
}

/**
 * The number a Call Now button dials. Every calls ad observed dials the number its
 * Facebook Page lists, so an empty field starts there; it can still be changed,
 * e.g. to a tracked number. A plain field rather than a country-picker phone
 * input: the server normalizes whatever is typed.
 */
function CallPhoneNumberField({
  value,
  disabled,
  onChange,
}: {
  value: string
  disabled: boolean
  onChange: (next: string) => void
}) {
  const businessID = useGetBusinessID()
  const { data: pagePhone } = useQuery(
    ['facebook-page-phone', businessID],
    () => facebookService.getFacebookPagePhone(businessID),
    // A failed lookup (no Page token, a Graph error) won't succeed on retry, and the
    // field is usable empty either way.
    { staleTime: Infinity, retry: false }
  )

  useEffect(() => {
    if (!value && pagePhone && !disabled) onChange(pagePhone)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagePhone])

  const valid = !!normalizeCallPhoneNumber(value)
  return (
    <Field
      label="Phone number"
      value={value}
      disabled={disabled}
      type="tel"
      note={
        !valid
          ? 'Enter the number people reach when they tap Call Now.'
          : pagePhone && value === pagePhone
          ? 'From your Facebook Page. Change it to use a different number, such as a tracked one.'
          : undefined
      }
      noteTone={valid ? dark.textMuted : undefined}
      onChange={onChange}
    />
  )
}

/** Which of this business's lead forms the ad's button opens, if any. */
function LeadCardField({
  value,
  disabled,
  onChange,
}: {
  value?: number
  disabled: boolean
  onChange: (leadCardID: number | null, leadCard?: LeadCard) => void
}) {
  const leadCards = useGetLeadCardsForBusiness(Number(useGetBusinessID()))
  const pick = (value: string) => {
    if (!value) return onChange(null)
    const leadCardID = Number(value)
    onChange(
      leadCardID,
      leadCards?.find(({ id }) => id === leadCardID)
    )
  }
  return (
    <Labelled label="Lead card">
      <select
        aria-label="Lead card"
        value={value ?? ''}
        disabled={disabled}
        onChange={e => pick(e.target.value)}
        style={modalInputStyle(dark)}
      >
        <option value="">None</option>
        {leadCards?.map(leadCard => (
          <option key={leadCard.id} value={leadCard.id}>
            {leadCard.formName}
          </option>
        ))}
      </select>
    </Labelled>
  )
}

/**
 * A field's label, with anything measured about the field's value on the same
 * line, right-aligned.
 *
 * Sentence case rather than the uppercase eyebrow the read-only metadata view
 * uses: five all-caps letterspaced labels over a form of inputs shout, and the
 * eyebrow treatment is there to mark a section, not to name a field.
 *
 * The counter shares the label's line because as a row of its own it cost every
 * field a third line — which is most of what made this column overflow — and it
 * sat closer to the next field's label than to the value it counted.
 */
function Labelled({
  label,
  meta,
  children,
}: {
  label: string
  meta?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span
          style={{
            fontFamily: typo.family.sans,
            fontSize: typo.size.body,
            fontWeight: typo.weight.medium,
            color: dark.textMuted,
          }}
        >
          {label}
        </span>
        {meta && <span style={{ marginLeft: 'auto' }}>{meta}</span>}
      </span>
      {children}
    </label>
  )
}

/**
 * A labelled copy field. The counter stays muted until the value passes
 * Facebook's recommended length and then turns amber — the same treatment the
 * importer gives it, because the fact worth surfacing is identical: over the
 * recommendation Facebook may truncate, over the hard cap the column won't hold it.
 */
function Field({
  label,
  value,
  disabled,
  rows,
  maxLength,
  recommendedMax,
  note,
  noteTone = statusColors.warn,
  type = 'text',
  onChange,
}: {
  label: string
  value: string
  disabled: boolean
  rows?: number
  maxLength?: number
  recommendedMax?: number
  /** Something true about this value that isn't its length. */
  note?: string
  /** A warning unless said otherwise. */
  noteTone?: string
  type?: 'text' | 'tel'
  onChange: (next: string) => void
}) {
  const over = recommendedMax !== undefined && value.length > recommendedMax
  return (
    <Labelled
      label={label}
      meta={
        recommendedMax !== undefined && (
          <span
            style={{
              fontFamily: typo.family.sans,
              fontSize: typo.size.eyebrow,
              color: over ? statusColors.warn : dark.textFaint,
            }}
          >
            {`${value.length}/${recommendedMax}${over ? ' · may truncate' : ''}`}
          </span>
        )
      }
    >
      {rows ? (
        <textarea
          aria-label={label}
          value={value}
          rows={rows}
          disabled={disabled}
          maxLength={maxLength}
          onChange={e => onChange(e.target.value)}
          style={{ ...modalInputStyle(dark), resize: 'none' }}
        />
      ) : (
        <input
          aria-label={label}
          type={type}
          value={value}
          disabled={disabled}
          maxLength={maxLength}
          onChange={e => onChange(e.target.value)}
          style={modalInputStyle(dark)}
        />
      )}
      {/* A sentence about the value, not a measurement of it — so it stays under
        the field where it can wrap, rather than in the label row. */}
      {note && (
        <span
          style={{
            fontFamily: typo.family.sans,
            fontSize: typo.size.eyebrow,
            color: noteTone,
          }}
        >
          {note}
        </span>
      )}
    </Labelled>
  )
}
